/** Longest side of the stored photo. Plenty for a bird on a phone or a desktop detail page. */
export const PHOTO_MAX_EDGE = 2048;

const JPEG_QUALITY = 0.85;

const CAMERA_TYPES = new Set(["image/jpeg", "image/heic", "image/heif"]);

/** A file already this small is sent as it is: re-encoding it would only cost quality. */
const LEAVE_ALONE_BYTES = 1024 * 1024;

/**
 * Shrinks a photo before it leaves the phone.
 *
 * A camera roll photo is 3–8 MB, which is slow on aviary wifi and mostly pixels nobody
 * will see. Scaled to fit 2048px and re-encoded as JPEG it is a few hundred KB. It also
 * turns a HEIC into a JPEG, which is what a desktop browser can show.
 *
 * It never makes things worse: if the browser cannot decode the file (HEIC on a desktop),
 * or the result is not smaller, the original goes up untouched and the server decides.
 */
export async function resizeForUpload(file: File): Promise<File> {
  // Only what a camera produces. A PNG or WebP can carry transparency, which JPEG cannot,
  // and painting a background in to cover that would need a colour this file may not own.
  if (!CAMERA_TYPES.has(file.type.toLowerCase()) || typeof createImageBitmap !== "function") {
    return file;
  }

  let bitmap: ImageBitmap;

  try {
    // `from-image` applies the EXIF rotation. Without it a portrait photo is re-encoded
    // on its side, because the canvas drops the orientation tag the original relied on.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file;
  }

  try {
    const scale = Math.min(1, PHOTO_MAX_EDGE / Math.max(bitmap.width, bitmap.height));

    if (scale === 1 && file.size <= LEAVE_ALONE_BYTES) {
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }

    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );

    if (!blob || blob.size >= file.size) {
      return file;
    }

    return new File([blob], `${file.name.replace(/\.[^.]+$/, "") || "photo"}.jpg`, {
      type: "image/jpeg",
    });
  } finally {
    bitmap.close();
  }
}
